import React, { useState, useEffect, useRef } from 'react';
import { calculateChart } from '../services/api';
import PositionsTable from './PositionsTable';
import './OnboardingFlow.css';

const INTEREST_OPTIONS = [
  {
    id: 'career',
    label: 'Career & Success',
    glyph: '☿',
    description: 'Professional growth, business ventures, and financial prosperity',
  },
  {
    id: 'relationships',
    label: 'Love & Relationships',
    glyph: '♀',
    description: 'Marriage, partnerships, and romantic compatibility',
  },
  {
    id: 'health',
    label: 'Health & Wellness',
    glyph: '☉',
    description: 'Physical well-being, vitality, and remedial measures',
  },
  {
    id: 'timing',
    label: 'Timing of Events',
    glyph: '♄',
    description: 'When important life events are likely to unfold',
  },
  {
    id: 'spiritual',
    label: 'Spiritual Growth',
    glyph: '♃',
    description: 'Dharma, karma, and the inner path',
  },
  {
    id: 'general',
    label: 'General Guidance',
    glyph: '☾',
    description: 'Overall life direction and opportunities',
  },
];

function OnboardingFlow({
  birthData,
  existingChart,
  onComplete,
  onChartCalculated,
  onBack,
}) {
  // If this profile already has a chart, skip straight past calculation.
  const [step, setStep] = useState(existingChart ? 'questions' : 'calculating');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [chartData, setChartData] = useState(existingChart || null);
  const [error, setError] = useState(null);
  const requestedRef = useRef(false);

  useEffect(() => {
    // Guard against double-invocation (React 18 StrictMode double-mounts
    // effects in development, which would fire two chart requests).
    if (requestedRef.current || existingChart || !birthData) return;
    requestedRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        const chartResult = await calculateChart(birthData);
        if (cancelled) return;
        setChartData(chartResult);
        onChartCalculated(chartResult);
        setStep('questions');
      } catch (err) {
        console.error('Chart calculation error:', err);
        if (!cancelled) setError('We could not calculate this chart. Please check the birth details and try again.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [birthData, existingChart, onChartCalculated]);

  const toggleInterest = (interestId) => {
    setSelectedInterests((prev) =>
      prev.includes(interestId)
        ? prev.filter((id) => id !== interestId)
        : [...prev, interestId]
    );
  };

  const retry = () => {
    setError(null);
    requestedRef.current = false;
    setStep('calculating');
  };

  if (error) {
    return (
      <div className="onboarding-flow">
        <div className="onboarding-card error-card">
          <div className="error-glyph" aria-hidden="true">✦</div>
          <h2>Something went wrong</h2>
          <p className="subtitle">{error}</p>
          <div className="error-actions">
            <button className="ghost-btn" onClick={onBack}>Edit details</button>
            <button className="continue-btn" onClick={retry}>Try again</button>
          </div>
        </div>
      </div>
    );
  }

  const dasha = chartData?.dasha?.current_mahadasha?.lord;

  return (
    <div className="onboarding-flow">
      {step === 'calculating' && (
        <div className="onboarding-card calculating-card">
          <div className="orbit" aria-hidden="true">
            <div className="orbit-ring orbit-ring-1" />
            <div className="orbit-ring orbit-ring-2" />
            <div className="orbit-ring orbit-ring-3" />
            <div className="orbit-core" />
          </div>
          <h2>Casting your chart</h2>
          <p className="subtitle">
            Computing planetary longitudes, house cusps, and Vimshottari dasha
            periods from the Swiss Ephemeris.
          </p>
        </div>
      )}

      {step === 'questions' && (
        <div className="onboarding-card">
          <span className="form-eyebrow">Step 2 of 2</span>
          <h2>What matters most to you?</h2>
          <p className="subtitle">
            Pick the areas you want to explore. You can ask about anything later.
          </p>

          {dasha && (
            <div className="chart-preview">
              {/* Lagna and Moon are in the positions table's legend below, so
                  only the running dasha is repeated here. */}
              {dasha && (
                <div className="preview-stat">
                  <span className="preview-label">Current Dasha</span>
                  <span className="preview-value">{dasha}</span>
                </div>
              )}
            </div>
          )}

          {chartData?.planets && (
            <PositionsTable chartData={chartData} embedded />
          )}

          <div className="interests-grid">
            {INTEREST_OPTIONS.map((option) => {
              const selected = selectedInterests.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  className={`interest-option ${selected ? 'selected' : ''}`}
                  onClick={() => toggleInterest(option.id)}
                  aria-pressed={selected}
                >
                  <span className="interest-glyph" aria-hidden="true">{option.glyph}</span>
                  <span className="interest-label">{option.label}</span>
                  <span className="interest-description">{option.description}</span>
                  <span className="interest-tick" aria-hidden="true">✓</span>
                </button>
              );
            })}
          </div>

          <button
            className="continue-btn"
            onClick={() => onComplete(selectedInterests)}
            disabled={selectedInterests.length === 0}
          >
            {selectedInterests.length === 0
              ? 'Select at least one'
              : `Continue with ${selectedInterests.length} selected`}
          </button>
        </div>
      )}
    </div>
  );
}

export default OnboardingFlow;
